import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../../external/components/Button/Button';
import {
  StyledDd,
  StyledDefWrapper,
  StyledDl,
  StyledDt,
} from '../../../external/components/DefLists';
import { Skeleton } from '../../../external/components/SkeletonLoading/Skeleton';
import Text from '../../../external/components/Text/Text';
import { formatToken } from '../../../helpers/token-helper';
import { State } from '../../../reducers';
import { NetworkId } from '../../../types/network.types';
import { ButtonContainer } from '../components/ButtonContainer';
import { HorizontalRule } from '../components/HorizontalRule';
import { BigNumberHelper } from '../helpers/BigNumbeHelper';
import { getNetworkText } from '../helpers/getNetworkText';
import { useUnstakeAvailable } from '../hooks/useCalculateStakeAvailable';
import { useLpStakingData } from '../hooks/useLpStakingData';
import { UseLpStakingPool } from '../hooks/usePool';
import { LpScreenEnum } from './_Screens';

interface Props {
  walletNetworkId: NetworkId;
  setScreen: React.Dispatch<React.SetStateAction<LpScreenEnum>>;
}

export function InitialScreen({ walletNetworkId, setScreen }: Props) {
  const [loading, setLoading] = useState(false);

  const pool = UseLpStakingPool();
  const walletAddress = useSelector(({ account }: State) => ({
    walletAddress: account.address,
  }));

  const {
    apr,
    stakedBalance,
    stakedBalanceUsd,
    rewardBalance: rewardBalanceStore,
    tokenBalance: tokenBalanceStore,
    unlockingTime: unlockingTimeStore,
    claimUnlockTime: claimUnlockTimeStore,
    rewardsEarned: rewardEarnedStore,
    unstakedBalance: unstakedBalanceStore,
    estimatedDailyReward: estimatedDailyRewardStore,
    rewardTokenSymbol,
    rewardTokenDecimals,
    stakingTokenSymbol,
    finished,
  } = pool;

  const {
    dailyReward: dailyRewardPool,
    rewardsEarned: rewardEarnedPool,
    rewardBalance: rewardBalPool,
    unstakedBalance: unstakeBalPool,
    tokenBalance: tokenBalPool,
    claimUnlockTime: ClaimUnlockPool,
    unlockingTime: unlockingTimePool,
    getTokenBalance,
    getDailyReward,
    getRewardsEarned,
    getRewardUnlockTime,
    getUnstakedBalance,
  } = useLpStakingData({
    pool,
  });

  const rewardBalance = rewardBalanceStore || rewardBalPool || '0';
  const tokenBalance = tokenBalanceStore || tokenBalPool;
  const claimUnlockTime = claimUnlockTimeStore || ClaimUnlockPool;
  const unlockingTime = unlockingTimeStore || unlockingTimePool;
  const dailyReward = estimatedDailyRewardStore || dailyRewardPool;
  const rewardsEarned = rewardEarnedStore || rewardEarnedPool;
  const unstakedBalance = unstakedBalanceStore || unstakeBalPool;

  const { isAvailable, timeUntil } = useUnstakeAvailable(
    parseInt(unlockingTime) || 0,
    parseInt(claimUnlockTime) || 0,
  );

  const rewardBalanceGreaterThanZero = BigNumberHelper.isGreaterThan(rewardBalance, '0');
  const withdrawalAmountGreatherThanZero = BigNumberHelper.isGreaterThan(unstakedBalance, '0');
  const stakedBalanceLessThanOrZero = BigNumberHelper.isLessThanOrEqualToZero(stakedBalance || '0');

  const init = async function () {
    setLoading(true);
    try {
      if (!pool.tokenBalance) {
        await getTokenBalance();
      }
      if (!pool.estimatedDailyReward) {
        await getDailyReward();
      }

      if (!rewardBalanceStore) {
        await getRewardsEarned();
      }

      if (!unlockingTimeStore) {
        await getRewardUnlockTime();
      }
      if (!rewardEarnedStore) {
        await getUnstakedBalance();
      }
    } catch (error) {}
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      {' '}
      <StyledDl>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Current {stakingTokenSymbol} balance
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              {loading ? (
                <Skeleton height='18px' width='100px' dark={true} />
              ) : (
                <>
                  <Text dark={true} as='span'>
                    {BigNumberHelper.formatBigNumb(tokenBalance || '0')} {stakingTokenSymbol}
                  </Text>
                </>
              )}
            </>
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Current amount staked
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              {loading ? (
                <Skeleton height='18px' width='200px' dark={true} />
              ) : (
                <>
                  <Text dark={true} as='span'>
                    {BigNumberHelper.formatBigNumb(stakedBalance || '0')} {stakingTokenSymbol}{' '}
                  </Text>
                  <Text color='green' as='span'>
                    (${parseFloat(stakedBalanceUsd)} USD)
                  </Text>
                </>
              )}
            </>
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Total rewards earned
            </Text>
          </StyledDt>
          <StyledDd>
            {' '}
            {loading ? (
              <Skeleton height='18px' width='100px' dark={true} />
            ) : (
              <>
                <Text dark={true} as='span'>
                  {parseFloat(rewardsEarned).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: rewardTokenDecimals,
                  })}{' '}
                  {rewardTokenSymbol}{' '}
                </Text>
              </>
            )}
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Estimated daily rewards
            </Text>
          </StyledDt>
          <StyledDd>
            {loading ? (
              <Skeleton height='18px' width='100px' dark={true} />
            ) : (
              <>
                <Text dark={true} as='span'>
                  {formatToken(dailyReward, 15)} {rewardTokenSymbol}
                </Text>
              </>
            )}
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Estimated Apr
            </Text>
          </StyledDt>
          <StyledDd>
            {loading ? (
              <Skeleton height='18px' width='100px' dark={true} />
            ) : (
              <>
                <Text dark={true} as='span'>
                  {apr} %
                </Text>
              </>
            )}
          </StyledDd>
        </StyledDefWrapper>
      </StyledDl>
      {walletNetworkId !== pool.networkId && (
        <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
          {' '}
          To start staking/unstaking in this {stakingTokenSymbol} staking pool, please connect to
          the {getNetworkText(pool.networkId)} network
        </Text>
      )}
      {loading ? (
        <ButtonContainer>
          <Skeleton height='36px' width='100px' dark={true} />
          <Skeleton height='36px' width='100px' dark={true} />
        </ButtonContainer>
      ) : (
        <>
          <ButtonContainer>
            {!finished ? (
              <Button
                dark={true}
                onClick={() => setScreen(LpScreenEnum.STAKE_SCREEN)}
                disabled={walletNetworkId !== pool.networkId}
              >
                {!stakedBalanceLessThanOrZero ? 'Stake more' : 'Stake'}
              </Button>
            ) : null}
            {!stakedBalanceLessThanOrZero && (
              <Button
                dark={true}
                variant='secondary'
                disabled={!stakedBalance || walletNetworkId !== pool.networkId}
                onClick={() => setScreen(LpScreenEnum.UNSTAKE_SCREEN)}
              >
                Unstake
              </Button>
            )}
          </ButtonContainer>
          <>
            <>
              <br />
              <HorizontalRule />
              <br />
              <StyledDefWrapper>
                {rewardBalanceGreaterThanZero ? (
                  <>
                    <StyledDt>
                      <Text dark={true} as='span' weight={600}>
                        Unclaimed rewards
                      </Text>
                    </StyledDt>
                    <StyledDd className='flex justify-space-between align-center'>
                      <Text dark={true} as='span'>
                        {BigNumberHelper.formatBigNumb(rewardBalance)} {rewardTokenSymbol}
                      </Text>{' '}
                      <Button
                        dark={true}
                        disabled={walletNetworkId !== pool.networkId}
                        variant='secondary'
                        onClick={() => setScreen(LpScreenEnum.CLAIM_CONFIRM)}
                      >
                        {' '}
                        Claim
                      </Button>
                    </StyledDd>
                  </>
                ) : null}
                {withdrawalAmountGreatherThanZero && (
                  <>
                    <StyledDt style={{ marginTop: '1rem' }}>
                      <Text dark={true} as='span' weight={600}>
                        Available for {rewardTokenSymbol} withdrawal
                      </Text>
                    </StyledDt>
                    <StyledDd className='flex justify-space-between align-center'>
                      <div>
                        <Text dark={true} as='span'>
                          {BigNumberHelper.formatBigNumb(unstakedBalance)} {rewardTokenSymbol}
                        </Text>{' '}
                        <Text color='green' style={{ margin: '0.5rem 0' }}>
                          {`Available ${isAvailable ? 'now for withdrawal' : ` in ${timeUntil}`}`}
                        </Text>
                      </div>
                      <Button
                        dark={true}
                        variant='secondary'
                        disabled={
                          !withdrawalAmountGreatherThanZero ||
                          !isAvailable ||
                          walletNetworkId !== pool.networkId
                        }
                        onClick={() => setScreen(LpScreenEnum.WITHDRAW_CONFIRM)}
                      >
                        {' '}
                        Withdraw
                      </Button>
                    </StyledDd>
                  </>
                )}
              </StyledDefWrapper>
            </>
          </>
        </>
      )}
      {walletAddress && (
        <>
          <HorizontalRule />
          <Text dark={true} weight={600}>
            Transaction history
          </Text>
          <Button
            dark={true}
            disabled={loading}
            variant='secondary'
            onClick={() => setScreen(LpScreenEnum.TRANSACTION_HISTORY)}
          >
            View transaction history
          </Button>
        </>
      )}
    </>
  );
}
